<template>
  <teleport to="body">
    <transition name="ui-dialog">
      <div
        v-if="show"
        class="fixed inset-0 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm sm:p-8"
        :style="{ zIndex: zIndex }"
        @click.self="onCancel"
      >
        <div
          ref="card"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel || title || undefined"
          tabindex="-1"
          class="ui-dialog-card flex max-h-full w-full min-w-0 flex-col rounded-lg bg-white shadow-xl outline-none"
          :class="[overflowVisible ? 'overflow-visible' : '', contentClass]"
          :style="{ maxWidth: computedMaxWidth, width: width || undefined }"
          @keydown.tab="trapTab"
        >
          <div
            v-if="$slots.header || title"
            class="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4"
          >
            <h3 class="text-base font-medium text-gray-900">{{ title }}</h3>
            <button
              v-if="!persistent"
              type="button"
              class="text-gray-400 hover:text-gray-600"
              :aria-label="$t('GENERAL.CLOSE')"
              @click="onCancel"
            >
              <UiIcon name="x" :size="20" />
            </button>
          </div>
          <div
            class="min-h-0 px-5 py-4 text-sm text-gray-700"
            :class="overflowVisible ? 'overflow-visible' : 'overflow-y-auto'"
          >
            <slot />
          </div>
          <div
            v-if="$slots.actions"
            class="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-5 py-3"
          >
            <slot name="actions" />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { type TranslateString } from '@/utils'

import UiIcon from './UiIcon.vue'

/** UiDialog — Vuetify <v-dialog> replacement (centered modal overlay).
 * Dual Vue 2 / Vue 3 compatible. */
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    value?: boolean
    title?: TranslateString
    persistent?: boolean
    maxWidth?: string
    /** Card width (the root is a teleport, so a `style` attr can't reach
     * the card — e.g. UiModal's `width="fit-content"`). */
    width?: string
    /** Extra classes for the card (Vuetify content-class). */
    contentClass?: string
    /** Let the content overflow the card instead of scrolling inside it
     * (UiModal `overflowing`: pickers that must escape the card). */
    overflowVisible?: boolean
    /** Accessible name when there is no `title` (UiModal renders its own). */
    ariaLabel?: string
  }>(),
  {
    persistent: false,
    maxWidth: '32rem',
    width: undefined,
    contentClass: undefined,
    overflowVisible: false,
  },
)

// Vue 2 defaults absent Boolean props to `false` (not `undefined`), so `??`
// won't fall through when v-model binds to `value` and `modelValue` is the
// default `false`. Use `||` so the default `false` falls through to `value`.
const show = computed(() => props.modelValue || props.value || false)

// Stacked dialogs: a dialog takes the z-index above the top-most OPEN one
// when it opens, and leaves the stack when it closes/unmounts — so z-index
// stays bounded (100 + 10 per open dialog) instead of growing on every mount,
// and the snackbar (2000) / teleported UiMenu (1000) stay above all dialogs.
// The stack lives in module scope (plain <script> below) — declared in
// <script setup> it would be per-instance, defeating stacking.
const zIndex = ref(DIALOG_BASE_Z + 10)
const stackToken = {}

function leaveStack() {
  const idx = openDialogs.findIndex((d) => d.token === stackToken)
  if (idx !== -1) openDialogs.splice(idx, 1)
}

watch(
  show,
  (isShown) => {
    if (isShown) {
      leaveStack()
      zIndex.value = (openDialogs.at(-1)?.z ?? DIALOG_BASE_Z) + 10
      openDialogs.push({ token: stackToken, z: zIndex.value })
    } else {
      leaveStack()
    }
  },
  { immediate: true },
)
onBeforeUnmount(leaveStack)

// Keyboard focus, as v-dialog did: move it into the dialog when it opens,
// keep Tab inside it, and give it back to the opener when it closes.
const card = ref<HTMLElement | null>(null)
let opener: HTMLElement | null = null
const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

watch(
  show,
  async (isShown) => {
    if (isShown) {
      opener =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      await nextTick()
      if (card.value && !card.value.contains(document.activeElement)) {
        card.value.focus({ preventScroll: true })
      }
    } else {
      restoreFocus()
    }
  },
  { immediate: true },
)
onBeforeUnmount(restoreFocus)

function restoreFocus() {
  if (opener?.isConnected === true) opener.focus({ preventScroll: true })
  opener = null
}

function trapTab(event: KeyboardEvent) {
  if (!card.value) return
  const focusables = Array.from(
    card.value.querySelectorAll<HTMLElement>(FOCUSABLE),
  ).filter((el) => el.offsetParent != null)
  if (focusables.length === 0) {
    event.preventDefault()
    return
  }
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement
  if (event.shiftKey && (active === first || active === card.value)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

const computedMaxWidth = computed(() => {
  if (props.maxWidth) {
    return `min(${props.maxWidth}, calc(100vw - 2rem))`
  }
  return 'calc(100vw - 2rem)'
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'cancel'): void
}>()

function onCancel() {
  if (props.persistent) return
  emit('update:modelValue', false)
  emit('cancel')
}
</script>

<script lang="ts">
// Module scope — shared by all UiDialog instances (see comment above).
const DIALOG_BASE_Z = 100
const openDialogs: { token: object; z: number }[] = []
</script>

<style scoped>
.ui-dialog-enter-active,
.ui-dialog-leave-active {
  transition: opacity 0.2s;
}
.ui-dialog-enter-from,
.ui-dialog-leave-to {
  opacity: 0;
}
.ui-dialog-card {
  transition: transform 0.2s;
}
.ui-dialog-enter-from .ui-dialog-card,
.ui-dialog-leave-to .ui-dialog-card {
  transform: scale(0.95);
}
</style>
